import { useVirtualizer } from "@tanstack/react-virtual";
import { debounce } from "lodash";
import {
  type HTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page as ReactPdfPage, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const MAX_PDF_WIDTH = 720;
const PADDING_RATIO = 0.96;
const PAGE_GAP = 8;
const OVERSCAN = 2;
const SCROLL_OFFSET = 15;

// Default aspect ratio (A4: 297/210 = √2) - used only as initial fallback
const DEFAULT_ASPECT_RATIO = Math.SQRT2;

type PageDimensions = {
  originalWidth: number;
  originalHeight: number;
  scaledHeight: number;
};

type Props = {
  pdfBlob?: string;
  coordinates: any;
  currentPage?: number;
  onVisiblePageChange?: (page: number) => void;
  onPdfLoad?: ({ totalPages }: { totalPages: number }) => void;
  enableProgrammaticScroll?: boolean;
  className?: HTMLAttributes<HTMLDivElement>["className"];
  renderOnlyOnePage?: boolean;
  maxWidth?: number;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  rotation?: number;
  onRotationChange?: (rotation: number) => void;
};

export type PDFViewerRef = {
  rotate: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  download: () => Promise<void>;
  totalPages: () => number;
  goToPage: (pageNumber: number) => void;
};

const PdfErrorCard = () => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-center items-center flex-1 bg-gray-200 w-full h-full">
      <Card className="w-[350px]">
        <CardHeader>
          <CardTitle className="text-left">
            {t("generic_error", "Fehler")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {t(
            "pdf_load_error",
            "Beim Laden des PDFs ist ein Fehler aufgetreten. Versuchen Sie die Seite neu zu laden oder wenden Sie sich an den Support.",
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PDFViewer = forwardRef<PDFViewerRef, Props>(function PDFViewer(
  {
    pdfBlob,
    currentPage: _currentPage = 0,
    coordinates,
    onVisiblePageChange,
    onPdfLoad,
    enableProgrammaticScroll = false,
    className,
    renderOnlyOnePage = false,
    maxWidth = MAX_PDF_WIDTH,
    zoom: controlledZoom,
    onZoomChange,
    rotation: controlledRotation,
    onRotationChange,
  }: Props,
  ref,
) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const coordinateRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number>(0);

  const [containerWidth, setContainerWidth] = useState(0);
  const [internalZoom, setInternalZoom] = useState(1);
  const [internalRotation, setInternalRotation] = useState(0);

  const userZoom = controlledZoom ?? internalZoom;
  const userRotation = controlledRotation ?? internalRotation;

  const setUserZoom = (value: number | ((prev: number) => number)) => {
    const newValue = typeof value === "function" ? value(userZoom) : value;
    if (controlledZoom === undefined) {
      setInternalZoom(newValue);
    }
    onZoomChange?.(newValue);
  };

  const setUserRotation = (value: number | ((prev: number) => number)) => {
    const newValue = typeof value === "function" ? value(userRotation) : value;
    if (controlledRotation === undefined) {
      setInternalRotation(newValue);
    }
    onRotationChange?.(newValue);
  };

  const [pageDimensions, setPageDimensions] = useState<
    Map<number, PageDimensions>
  >(new Map());
  const [pdfError, setPdfError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (pdfBlob) {
      setPageDimensions(new Map());
      setPdfError(false);
      setNumPages(0);
      setIsLoaded(false);
    }
  }, [pdfBlob]);

  const debouncedSetContainerWidth = useMemo(
    () =>
      debounce((width: number) => {
        setContainerWidth(width);
      }, 100),
    [],
  );

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    setContainerWidth(containerRef.current.clientWidth);

    const observer = new ResizeObserver((entries) => {
      debouncedSetContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      debouncedSetContainerWidth.cancel();
    };
  }, [debouncedSetContainerWidth]);

  const fallbackPageHeight = useMemo(() => {
    const targetWidth = Math.min(containerWidth * PADDING_RATIO, maxWidth);
    return targetWidth * DEFAULT_ASPECT_RATIO;
  }, [containerWidth, maxWidth]);

  const referenceHeight = useMemo(() => {
    const firstLoaded = pageDimensions.values().next().value;
    return firstLoaded?.scaledHeight ?? fallbackPageHeight;
  }, [pageDimensions, fallbackPageHeight]);

  const referenceAspectRatio = useMemo(() => {
    const firstLoaded = pageDimensions.values().next().value;
    if (firstLoaded) {
      return firstLoaded.originalHeight / firstLoaded.originalWidth;
    }
    return DEFAULT_ASPECT_RATIO;
  }, [pageDimensions]);

  const estimatePageHeight = useCallback(
    (index: number) => {
      const dimensions = pageDimensions.get(index);
      if (dimensions !== undefined) {
        return dimensions.scaledHeight * userZoom + PAGE_GAP;
      }
      return referenceHeight * userZoom + PAGE_GAP;
    },
    [pageDimensions, userZoom, referenceHeight],
  );

  const virtualizer = useVirtualizer({
    count: numPages,
    getScrollElement: () => containerRef.current,
    estimateSize: estimatePageHeight,
    overscan: OVERSCAN,
    measureElement: undefined,
  });

  const handlePageLoad = useCallback(
    (
      index: number,
      originalWidth: number,
      originalHeight: number,
      scaledHeight: number,
    ) => {
      setPageDimensions((prev) => {
        const newMap = new Map(prev);
        newMap.set(index, { originalWidth, originalHeight, scaledHeight });
        return newMap;
      });
      virtualizer.measure();
    },
    [virtualizer],
  );

  const virtualItems = virtualizer.getVirtualItems();

  const scrollOffset = virtualizer.scrollOffset ?? 0;
  const visiblePageIndex = useMemo(() => {
    for (const item of virtualItems) {
      const itemEnd = item.start + item.size;
      if (itemEnd > scrollOffset) {
        return item.index;
      }
    }
    return virtualItems[0]?.index ?? 0;
  }, [virtualItems, scrollOffset]);

  useEffect(() => {
    if (!onVisiblePageChange) return;
    onVisiblePageChange(visiblePageIndex + 1);
  }, [visiblePageIndex, onVisiblePageChange]);

  const scrollRatioRef = useRef<{ x: number; y: number } | null>(null);
  const prevZoomRef = useRef(userZoom);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (prevZoomRef.current !== userZoom && scrollRatioRef.current) {
      const { x: ratioX, y: ratioY } = scrollRatioRef.current;
      container.scrollLeft =
        ratioX * container.scrollWidth - container.clientWidth / 2;
      container.scrollTop =
        ratioY * container.scrollHeight - container.clientHeight / 2;
      scrollRatioRef.current = null;
    }

    prevZoomRef.current = userZoom;
    virtualizer.measure();
  }, [userZoom, virtualizer]);

  const zoomIn = () => {
    const container = containerRef.current;
    if (container) {
      const scrollCenterX = container.scrollLeft + container.clientWidth / 2;
      const scrollCenterY = container.scrollTop + container.clientHeight / 2;
      scrollRatioRef.current = {
        x: scrollCenterX / container.scrollWidth,
        y: scrollCenterY / container.scrollHeight,
      };
    }

    const newZoom = Math.round(Math.min(userZoom + 0.2, 3) * 10) / 10;
    setUserZoom(newZoom);
  };

  const zoomOut = () => {
    const container = containerRef.current;
    if (container) {
      const scrollCenterX = container.scrollLeft + container.clientWidth / 2;
      const scrollCenterY = container.scrollTop + container.clientHeight / 2;
      scrollRatioRef.current = {
        x: scrollCenterX / container.scrollWidth,
        y: scrollCenterY / container.scrollHeight,
      };
    }

    const newZoom = Math.round(Math.max(userZoom - 0.2, 0.5) * 10) / 10;
    setUserZoom(newZoom);
  };

  const handleRotation = () => {
    setUserRotation(((userRotation ?? 0) + 90) % 360);
  };

  const handleDownload = async () => {
    try {
      if (!pdfBlob) return;

      const a = document.createElement("a");
      a.href = pdfBlob;
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", "_")
        .replace(/:/g, "-");
      a.download = `rechnung_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      toast.error(t("generic_error"));
    }
  };

  const goToPage = useCallback(
    (pageNumber: number) => {
      const index = Math.max(0, Math.min(pageNumber - 1, numPages - 1));
      virtualizer.scrollToIndex(index, { align: "start" });
    },
    [virtualizer, numPages],
  );

  useEffect(() => {
    if (enableProgrammaticScroll && _currentPage > 0) {
      goToPage(_currentPage + 1);
    }
  }, [enableProgrammaticScroll, _currentPage, goToPage]);

  useImperativeHandle(ref, () => ({
    rotate: handleRotation,
    zoomIn,
    zoomOut,
    download: handleDownload,
    totalPages: () => numPages ?? 0,
    goToPage,
  }));

  useEffect(() => {
    if (numPages > 0 && onPdfLoad) {
      onPdfLoad({ totalPages: numPages });
    }
  }, [numPages, onPdfLoad]);

  useEffect(() => {
    if (!coordinates || !numPages) return;

    const container = containerRef.current;
    if (!container) return;

    const pageIndex = (coordinates.pageNumber ?? 1) - 1;

    const pageStartOffset =
      virtualizer.getOffsetForIndex(pageIndex, "start")?.[0] ?? 0;

    const dimensions = pageDimensions.get(pageIndex);
    const pageHeight = dimensions?.scaledHeight ?? referenceHeight;
    const originalPageHeight = dimensions?.originalHeight ?? 842; // Fallback to A4 only if no data

    const coordinateRatio = coordinates.top / originalPageHeight;
    const scaledCoordinateOffset = coordinateRatio * pageHeight * userZoom;

    const targetScrollTop =
      pageStartOffset + scaledCoordinateOffset - SCROLL_OFFSET;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  }, [
    coordinates,
    numPages,
    virtualizer,
    pageDimensions,
    referenceHeight,
    userZoom,
  ]);

  if (renderOnlyOnePage) {
    return (
      <div
        ref={containerRef}
        className={cn("w-full h-full overflow-y-auto bg-gray-200", className)}
        style={{ scrollbarGutter: "stable" }}
      >
        {pdfError ? (
          <PdfErrorCard />
        ) : pdfBlob ? (
          <div
            className={cn(
              "min-h-full py-2 transition-opacity duration-200",
              isLoaded ? "opacity-100" : "opacity-0",
            )}
          >
            <Document
              key={pdfBlob}
              file={pdfBlob}
              onLoadSuccess={(doc) => {
                setNumPages(doc.numPages);
                requestAnimationFrame(() => setIsLoaded(true));
              }}
              onError={(e) => {
                console.error(e);
                setPdfError(true);
              }}
              loading={null}
            >
              <Page
                index={_currentPage}
                userZoom={userZoom}
                rotation={userRotation}
                coordinates={coordinates}
                containerWidth={containerWidth}
                maxWidth={maxWidth}
                onPageLoad={handlePageLoad}
                coordinateRef={coordinateRef}
                referenceAspectRatio={referenceAspectRatio}
              />
            </Document>
          </div>
        ) : null}
      </div>
    );
  }

  if (pdfError) {
    return (
      <div
        ref={containerRef}
        className={cn("w-full h-full overflow-y-auto bg-gray-200", className)}
      >
        <PdfErrorCard />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn("w-full h-full overflow-y-auto bg-gray-200", className)}
      style={{ scrollbarGutter: "stable" }}
    >
      {pdfBlob ? (
        <Document
          key={pdfBlob}
          file={pdfBlob}
          onLoadSuccess={(doc) => {
            setNumPages(doc.numPages);
            requestAnimationFrame(() => setIsLoaded(true));
          }}
          onError={() => setPdfError(true)}
          loading={null}
        >
          <div
            className={cn(
              "relative w-full py-2 transition-opacity duration-200",
              isLoaded ? "opacity-100" : "opacity-0",
            )}
            style={{
              height: `${virtualizer.getTotalSize()}px`,
            }}
          >
            {containerWidth > 0 &&
              virtualItems.map((virtualItem) => (
                <div
                  key={virtualItem.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <Page
                    index={virtualItem.index}
                    userZoom={userZoom}
                    rotation={userRotation}
                    coordinates={coordinates}
                    containerWidth={containerWidth}
                    maxWidth={maxWidth}
                    onPageLoad={handlePageLoad}
                    coordinateRef={coordinateRef}
                    referenceAspectRatio={referenceAspectRatio}
                  />
                </div>
              ))}
          </div>
        </Document>
      ) : null}
    </div>
  );
});

const Page = ({
  index,
  rotation,
  coordinates,
  containerWidth,
  maxWidth,
  userZoom,
  onPageLoad,
  coordinateRef,
  referenceAspectRatio,
}: {
  rotation: number;
  coordinates: any;
  containerWidth: number;
  index: number;
  maxWidth: number;
  userZoom: number;
  onPageLoad: (
    index: number,
    originalWidth: number,
    originalHeight: number,
    scaledHeight: number,
  ) => void;
  coordinateRef: React.RefObject<HTMLDivElement>;
  referenceAspectRatio: number;
}) => {
  const [pageOriginalWidth, setPageOriginalWidth] = useState<number | null>(
    null,
  );
  const [pageOriginalHeight, setPageOriginalHeight] = useState<number | null>(
    null,
  );

  const devicePixelRatio = useMemo(
    () => Math.max(2, Math.min(window.devicePixelRatio || 1, 3)),
    [],
  );

  const targetWidth = Math.min(containerWidth * PADDING_RATIO, maxWidth);

  const fitScale =
    pageOriginalWidth && containerWidth ? targetWidth / pageOriginalWidth : 1;

  const finalScale = fitScale * userZoom;

  const scaledWidth = pageOriginalWidth ? pageOriginalWidth * finalScale : 0;
  const sidePadding = Math.max((containerWidth - scaledWidth) / 2, 0);

  useEffect(() => {
    if (pageOriginalWidth !== null && pageOriginalHeight !== null) {
      const scaledHeight = pageOriginalHeight * fitScale;
      onPageLoad(index, pageOriginalWidth, pageOriginalHeight, scaledHeight);
    }
  }, [pageOriginalWidth, pageOriginalHeight, fitScale, index, onPageLoad]);

  return (
    <div
      style={{ paddingLeft: sidePadding, paddingRight: sidePadding }}
      className="pb-2"
    >
      <ReactPdfPage
        pageNumber={index + 1}
        rotate={rotation}
        renderAnnotationLayer={false}
        renderForms={false}
        renderTextLayer={true}
        devicePixelRatio={devicePixelRatio}
        onLoadSuccess={(page) => {
          setPageOriginalWidth(page.originalWidth);
          setPageOriginalHeight(page.originalHeight);
        }}
        scale={finalScale}
        loading={null}
        canvasBackground="white"
      >
        {coordinates && coordinates.pageNumber === index + 1 ? (
          <CoordinateBox
            scaleFactor={finalScale}
            coordinates={coordinates}
            coordinateRef={coordinateRef}
          />
        ) : null}
      </ReactPdfPage>
    </div>
  );
};

const CoordinateBox = ({
  scaleFactor,
  coordinates,
  coordinateRef,
}: {
  scaleFactor: number;
  coordinates: any;
  coordinateRef: React.RefObject<HTMLDivElement>;
}) => {
  const PADDING = 4;

  return (
    <div
      ref={coordinateRef}
      style={{
        border: "2px solid #02DAF2",
        backgroundColor: "rgba(2, 218, 242, 0.1)",
        borderRadius: "5px",
        position: "absolute",
        left: `${(coordinates.left - PADDING) * scaleFactor}px`,
        top: `${(coordinates.top - PADDING) * scaleFactor}px`,
        width: `${(coordinates.width + PADDING * 2) * scaleFactor}px`,
        height: `${(coordinates.height + PADDING * 2) * scaleFactor}px`,
        zIndex: 2,
      }}
    />
  );
};
